import { Paperclip } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { Badge, Button, Card, ErrorState, LoadingState } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { PanelAndClaims } from '@/features/panel-history/PanelAndClaims';
import {
  openAttachment,
  usePatientRecord,
  type PatientRecord,
} from '@/features/encounters/api';
import { countryName } from '@/features/patients/countries';
import {
  ASSISTANCE_LABELS,
  CONSENT_LABELS,
  CONTACT_LABELS,
  COUNTER_CONSENTS,
} from '@/features/patients/types';
import { PageContainer } from '@/layouts/PageContainer';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import { klTime } from '@/pages/calendar/time';
import { DocumentList } from '@/pages/clinical/DocumentList';
import { HomeReadingsCard } from '@/pages/clinical/HomeReadingsCard';
import { AllergyList } from './AllergyList';
import { CancellablePrescriptions } from './CancellablePrescriptions';
import { PatientAppAccount } from './PatientAppAccount';
import { PatientBills } from './PatientBills';
import { PatientBloodType } from './PatientBloodType';
import { PatientCoverages } from './PatientCoverages';
import { PatientDetailsDrawer } from './PatientDetailsDrawer';
import { PatientIdentityText } from './PatientIdentityText';
import { PatientPhoto } from './PatientPhoto';
import { RecordResults } from './RecordResults';
import styles from './patients.module.css';
import { CLINIC_TZ } from '@/lib/clinic-time';

type RecordTabName =
  'Visits' | 'Results' | 'Documents' | 'Vitals' | 'Panel & Claims' | 'Billing';

const RECORD_TABS: RecordTabName[] = [
  'Visits',
  'Results',
  'Documents',
  'Vitals',
];

/**
 * "Record → the full patient record" (RL-723: "Open full record: visits,
 * results, documents"): every visit with its encounter (open or closed, each
 * linked), reviewed results, issued documents with their QR, attachments and
 * vitals over time; identity, consents and contacts alongside. Records merged
 * into this one are included. Every opening is audited.
 */
export function PatientRecordPage() {
  const { id } = useParams();
  const { data: rec, isLoading, error, refetch } = usePatientRecord(id);
  const { hasPermission } = useSession();
  const [drawer, setDrawer] = useOptionalUrlState('drawer');
  const editing = drawer === 'details';
  const [tab, setTab] = useUrlState<RecordTabName>('tab', 'Visits', [
    'Visits',
    'Results',
    'Documents',
    'Vitals',
    'Panel & Claims',
    'Billing',
  ]);

  if (isLoading) return <LoadingState />;
  if (error || !rec)
    return <ErrorState error={error} onRetry={() => void refetch()} />;
  const p = rec.patient;
  const tabs: RecordTabName[] = [
    ...RECORD_TABS,
    ...(hasPermission('patients:read') ? ['Panel & Claims' as const] : []),
    // RL-1049: the patient's bills at the current branch.
    ...(hasPermission('billing:read') ? ['Billing' as const] : []),
  ];
  const address = [
    p.address.line1,
    p.address.line2,
    p.address.postcode,
    p.address.city,
    p.address.state,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <PageContainer>
      {p.status === 'merged' && p.mergedInto && (
        <p className={styles.warnNote}>
          This record was merged into {p.mergedInto.fullName}. Use the kept
          record.
        </p>
      )}
      <Card
        title="Visits, results and documents"
        actions={
          <div className={styles.tabsInline} role="tablist" aria-label="Record">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={styles.tabInline}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>
        }
      >
        <RecordTab
          tab={tab}
          rec={rec}
          // RL-1859: nurses (vitals:read) see vitals history, not the notes.
          seesVitals={rec.clinical || hasPermission('vitals:read')}
        />
      </Card>
      <div className={styles.recordGrid}>
        <Card
          title={p.fullName}
          actions={
            <>
              <Badge tone={p.status === 'active' ? 'success' : 'neutral'} dot>
                {p.status}
              </Badge>
              {hasPermission('patients:update') && p.status !== 'merged' && (
                <Button size="sm" onClick={() => setDrawer('details')}>
                  Edit details
                </Button>
              )}
            </>
          }
        >
          <dl className={styles.rows}>
            {p.identities
              .filter((i) => !i.endedAt)
              .map((i) => (
                <div key={i.id} className={styles.rowPair}>
                  <dt>{i.type === 'mykad' ? 'MyKad' : 'Passport'}</dt>
                  <dd>
                    <PatientIdentityText identity={i} />
                  </dd>
                </div>
              ))}
            <dt>Nationality</dt>
            <dd>{countryName(p.nationality)}</dd>
            <dt>Date of birth</dt>
            <dd>{p.dateOfBirth ?? '—'}</dd>
            <dt>Sex</dt>
            <dd>{p.sex ?? '—'}</dd>
            <dt>Phone</dt>
            <dd>{p.phone ?? '—'}</dd>
            <dt>Email</dt>
            <dd>{p.email ?? '—'}</dd>
            <dt>Address</dt>
            <dd>{address || '—'}</dd>
            <dt>Registered at</dt>
            <dd>
              {p.registeredBranch.name} ·{' '}
              {new Date(p.createdAt).toLocaleDateString('en-GB', {
                timeZone: CLINIC_TZ,
              })}
            </dd>
            <dt>Coverage</dt>
            <dd>
              <PatientCoverages patient={p} />
            </dd>
            <dt>eKYC</dt>
            <dd>{p.ekyc.status === 'verified' ? 'Verified' : 'Unverified'}</dd>
            <dt>App account</dt>
            <dd>
              <PatientAppAccount patient={p} />
            </dd>
            <dt>Photo</dt>
            <dd>
              <PatientPhoto patient={p} />
            </dd>
            <dt>Blood type</dt>
            <dd>
              <PatientBloodType patient={p} />
            </dd>
            <dt>Allergies</dt>
            <dd>
              <AllergyList allergies={p.allergies} patientId={p.id} />
            </dd>
            {p.mergedFrom.length > 0 && (
              <>
                <dt>Merged in</dt>
                <dd>{p.mergedFrom.map((m) => m.fullName).join(', ')}</dd>
              </>
            )}
          </dl>
        </Card>
        <div className={styles.stack}>
          <Card title="Consents">
            <ul className={styles.list}>
              {COUNTER_CONSENTS.map((c) => (
                <li key={c}>
                  <span>{CONSENT_LABELS[c]}</span>
                  <Badge tone={p.consents[c]?.granted ? 'success' : 'neutral'}>
                    {p.consents[c]?.granted ? 'Given' : 'Not given'}
                  </Badge>
                </li>
              ))}
            </ul>
          </Card>
          {hasPermission('prescriptions:cancel') && (
            <CancellablePrescriptions patientId={p.id} />
          )}
          <Card title="Assistance needs">
            <p>
              {p.assistanceNeeds.length
                ? p.assistanceNeeds.map((n) => ASSISTANCE_LABELS[n]).join(', ')
                : 'None recorded'}
            </p>
          </Card>
          <Card title="Contacts">
            {p.contacts.length === 0 ? (
              <p className={styles.note}>
                No emergency contact, next of kin or safe contact recorded.
              </p>
            ) : (
              <ul className={styles.list}>
                {p.contacts.map((c) => (
                  <li key={c.id}>
                    <span>
                      {c.name}
                      {c.relationship ? ` (${c.relationship})` : ''} · {c.phone}
                    </span>
                    <Badge>{CONTACT_LABELS[c.kind]}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
      {hasPermission('chronic_care:read') && (
        <HomeReadingsCard
          patientId={p.id}
          canManage={hasPermission('chronic_care:manage')}
        />
      )}
      <PatientDetailsDrawer
        patientId={editing ? p.id : null}
        onClose={() => setDrawer(null)}
      />
    </PageContainer>
  );
}

const STATUS: Record<string, string> = {
  waiting: 'Waiting',
  in_room: 'In room',
  done: 'Done',
  cancelled: 'Cancelled',
};

function RecordTab({
  tab,
  rec,
  seesVitals,
}: {
  tab: RecordTabName;
  rec: PatientRecord;
  seesVitals: boolean;
}) {
  if (tab === 'Visits')
    return rec.visits.length === 0 ? (
      <p className={styles.note}>No visits yet.</p>
    ) : (
      <ul className={styles.visitList}>
        {rec.visits.map((v) => (
          <li key={v.ticketId ?? v.encounter?.id}>
            <div>
              <p>
                <strong>
                  {new Date(v.arrivedAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}{' '}
                  · {klTime(v.arrivedAt)}
                </strong>{' '}
                · {v.branch.name}
                {v.number !== null ? ` · ticket ${v.number}` : ''}
              </p>
              <p className={styles.sub}>
                {v.service?.name ?? 'Visit'} · {v.payer?.name ?? 'Self'}
                {v.encounter ? ` · ${v.encounter.doctor.fullName}` : ''}
              </p>
              {v.encounter && v.encounter.diagnoses.length > 0 && (
                <p className={styles.sub}>
                  {v.encounter.diagnoses
                    .map((d) => `${d.code} ${d.description}`)
                    .join(' · ')}
                </p>
              )}
            </div>
            <div className={styles.actions}>
              <Badge tone={v.status === 'done' ? 'neutral' : 'info'}>
                {STATUS[v.status] ?? v.status}
              </Badge>
              {v.encounter && rec.clinical && (
                <Link to={`/consultations/${v.encounter.id}`}>
                  {v.encounter.status === 'open'
                    ? 'Open consultation'
                    : `Encounter${v.encounter.addenda ? ` · ${v.encounter.addenda} addenda` : ''}`}
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  if (tab === 'Results') return <RecordResults rec={rec} />;
  if (tab === 'Panel & Claims')
    return <PanelAndClaims patientId={rec.patient.id} />;
  if (tab === 'Billing') return <PatientBills patientId={rec.patient.id} />;
  if (tab === 'Documents')
    return (
      <div className={styles.stack}>
        {rec.documents.length === 0 && rec.attachments.length === 0 && (
          <p className={styles.note}>No documents yet.</p>
        )}
        <DocumentList documents={rec.documents} />
        {rec.attachments.map((a) => (
          <button
            key={a.id}
            type="button"
            className={styles.inlineLink}
            onClick={() => void openAttachment(a.id)}
          >
            <Paperclip size={14} /> {a.fileName} ·{' '}
            {new Date(a.addedAt).toLocaleDateString('en-GB', {
              timeZone: CLINIC_TZ,
            })}
          </button>
        ))}
      </div>
    );
  return !seesVitals ? (
    <p className={styles.note}>Vitals are shown to clinical roles only.</p>
  ) : rec.vitals.length === 0 ? (
    <p className={styles.note}>No vitals recorded.</p>
  ) : (
    <table className={styles.compare}>
      <thead>
        <tr>
          <th>Taken</th>
          <th>BP</th>
          <th>Pulse</th>
          <th>Temp</th>
          <th>SpO2</th>
          <th>Weight</th>
          <th>BMI</th>
          <th>Glucose</th>
        </tr>
      </thead>
      <tbody>
        {rec.vitals.map((v) => (
          <tr key={v.id}>
            <td>
              {new Date(v.recordedAt).toLocaleString('en-GB', {
                timeZone: CLINIC_TZ,
              })}
            </td>
            <td>
              {v.bpSystolic ?? '—'}/{v.bpDiastolic ?? '—'}
            </td>
            <td>{v.pulse ?? '—'}</td>
            <td>{v.temperatureC ?? '—'}</td>
            <td>{v.spo2 ?? '—'}</td>
            <td>{v.weightKg ?? '—'}</td>
            <td>{v.bmi ?? '—'}</td>
            <td>{v.glucoseMmol ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
